import { useState, useCallback } from 'react';
import { validatePhoneNumber, PhoneValidationResult, formatPhoneNumber } from '../utils/phoneValidation';

interface UsePhoneValidationOptions {
  onValidationChange?: (validation: PhoneValidationResult) => void;
  onApiError?: (error: string) => void;
}

interface UsePhoneValidationReturn {
  phoneNumber: string;
  validation: PhoneValidationResult;
  apiError: string | null;
  setPhoneNumber: (phone: string) => void;
  validatePhone: (phone: string) => PhoneValidationResult;
  clearApiError: () => void;
  setApiError: (error: string) => void;
  isPhoneValid: boolean;
  formattedPhone: string;
}

export function usePhoneValidation(options: UsePhoneValidationOptions = {}): UsePhoneValidationReturn {
  const [phoneNumber, setPhoneNumberState] = useState('');
  const [validation, setValidationState] = useState<PhoneValidationResult>({
    isValid: false,
    formattedNumber: '',
    isInternational: false
  });
  const [apiError, setApiErrorState] = useState<string | null>(null);

  const validatePhone = useCallback((phone: string): PhoneValidationResult => {
    const result = validatePhoneNumber(phone);
    setValidationState(result);
    
    if (options.onValidationChange) {
      options.onValidationChange(result);
    }
    
    return result;
  }, [options.onValidationChange]);

  const setPhoneNumber = useCallback((phone: string) => {
    setPhoneNumberState(phone);
    validatePhone(phone);
    
    // Clear API error when user starts typing
    if (apiError) {
      setApiErrorState(null);
    }
  }, [validatePhone, apiError]);

  const clearApiError = useCallback(() => {
    setApiErrorState(null);
  }, []);

  const setApiError = useCallback((error: string) => {
    setApiErrorState(error);
    
    if (options.onApiError) {
      options.onApiError(error);
    }
  }, [options.onApiError]);

  // Check if the phone number is valid for API submission
  const isPhoneValid = validation.isValid && !apiError;

  // Get the formatted phone number for API calls
  const formattedPhone = validation.isValid ? validation.formattedNumber : phoneNumber;

  return {
    phoneNumber,
    validation,
    apiError,
    setPhoneNumber,
    validatePhone,
    clearApiError,
    setApiError,
    isPhoneValid,
    formattedPhone
  };
}

/**
 * Hook for handling API errors related to phone numbers
 */
export function usePhoneApiError() {
  const [apiError, setApiError] = useState<string | null>(null);

  const handleApiError = useCallback((error: any) => {
    let errorMessage = 'An error occurred';
    
    if (error?.response?.data?.error) {
      const apiError = error.response.data.error.toLowerCase();
      
      // Check for phone-related errors
      if (apiError.includes('phone') || apiError.includes('número') || apiError.includes('teléfono')) {
        if (apiError.includes('invalid') || apiError.includes('inválido') || apiError.includes('formato')) {
          errorMessage = 'Invalid phone number format';
        } else if (apiError.includes('exists') || apiError.includes('existe') || apiError.includes('duplicate')) {
          errorMessage = 'This phone number is already registered';
        } else if (apiError.includes('not found') || apiError.includes('no encontrado')) {
          errorMessage = 'Phone number not found';
        } else {
          errorMessage = 'Phone number error: ' + error.response.data.error;
        }
      } else {
        errorMessage = error.response.data.error;
      }
    } else if (error?.message) {
      errorMessage = error.message;
    }
    
    setApiError(errorMessage);
    return errorMessage;
  }, []);

  const clearError = useCallback(() => {
    setApiError(null);
  }, []);

  return {
    apiError,
    handleApiError,
    clearError
  };
}

/**
 * Hook for managing phone input with real-time validation
 */
export function usePhoneInput(initialValue: string = '') {
  const [value, setValue] = useState(initialValue);
  const [validation, setValidation] = useState<PhoneValidationResult>({
    isValid: false,
    formattedNumber: '',
    isInternational: false
  });

  const handleChange = useCallback((newValue: string, newValidation: PhoneValidationResult) => {
    setValue(newValue);
    setValidation(newValidation);
  }, []);

  const handleBlur = useCallback((newValidation: PhoneValidationResult) => {
    setValidation(newValidation);
  }, []);

  return {
    value,
    validation,
    handleChange,
    handleBlur,
    setValue
  };
} 